/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Google Drive picker — opens Google's own Drive browser (folders, search) and
 * returns the selected image. Uses the least-privilege `drive.file` scope (the app
 * only ever sees the file you pick) so no Google restricted-scope verification is
 * needed. Requires two build-time vars: VITE_GOOGLE_CLIENT_ID + VITE_GOOGLE_API_KEY.
 */

import { apiFetch } from './api.js';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string | undefined;
const SCOPE = 'https://www.googleapis.com/auth/drive.file';
// The Cloud project number (the prefix of the OAuth client id). Under the
// least-privilege `drive.file` scope, the Picker only grants download access to a
// picked file when it knows this app id — without it, the pick works but the
// subsequent Drive download is refused.
const APP_ID = CLIENT_ID?.split('-')[0];

export const driveConfigured = !!(CLIENT_ID && API_KEY);

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

let ready = false;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

async function ensureReady(): Promise<void> {
  if (ready) return;
  await Promise.all([
    loadScript('https://accounts.google.com/gsi/client'),
    loadScript('https://apis.google.com/js/api.js'),
  ]);
  await new Promise<void>((resolve) => window.gapi.load('picker', () => resolve()));
  ready = true;
}

// A Google access token is good for ~1 hour. Cache it (in memory) and reuse it so
// we don't re-prompt on every single Drive pick — the earlier behavior that popped
// the Google auth modal each time. Only when it's near expiry do we ask again, and
// then silently (prompt: '') since the grant already exists.
let cachedToken: { token: string; expiresAt: number } | null = null;

/**
 * Get a Google access token for the Picker.
 *  1. Reuse a still-valid cached token (no work).
 *  2. If the org is passed, ask our server to mint one from the stored refresh
 *     token — the "eternal" path: no Google prompt, even weeks later.
 *  3. Fall back to the interactive browser grant (prompts once per session) when
 *     the user hasn't connected Google yet, or the server has no token.
 */
async function getToken(orgId?: string | null): Promise<string> {
  if (cachedToken && cachedToken.expiresAt - Date.now() > 60_000) {
    return cachedToken.token;
  }
  if (orgId) {
    try {
      const d = await apiFetch<{ access_token: string; expires_in: number }>(
        `/api/orgs/${orgId}/drive/token`,
        { method: 'POST' },
      );
      cachedToken = {
        token: d.access_token,
        expiresAt: Date.now() + (Number(d.expires_in) || 3600) * 1000,
      };
      return d.access_token;
    } catch {
      // not connected / needs reconnect → fall through to the interactive grant
    }
  }
  return new Promise<string>((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPE,
      callback: (resp: { access_token?: string; expires_in?: number; error?: string }) => {
        if (resp.access_token) {
          cachedToken = {
            token: resp.access_token,
            expiresAt: Date.now() + (Number(resp.expires_in) || 3600) * 1000,
          };
          resolve(resp.access_token);
        } else {
          reject(new Error(resp.error || 'Google Drive authorization was cancelled.'));
        }
      },
    });
    // prompt '' → shows consent the first time only; silent on subsequent refreshes
    // once the user has granted access.
    client.requestAccessToken({ prompt: '' });
  });
}

export interface DrivePick {
  file: File;
}

/** Open the Drive picker (given view types) and download the pick as a File. */
async function pickFromDrive(
  viewIds: ('DOCS_IMAGES' | 'DOCS_VIDEOS')[],
  parentId?: string | null,
  orgId?: string | null,
): Promise<DrivePick | null> {
  if (!driveConfigured) throw new Error('Google Drive is not configured.');
  await ensureReady();
  const token = await getToken(orgId);
  const picker = window.google.picker;

  const IMAGE_MIMES = 'image/jpeg,image/png,image/webp,image/gif';
  const VIDEO_MIMES = 'video/mp4,video/quicktime,video/webm';

  const wantsVideo = viewIds.includes('DOCS_VIDEOS');
  const mimes = wantsVideo ? `${IMAGE_MIMES},${VIDEO_MIMES}` : IMAGE_MIMES;

  const doc = await new Promise<any | null>((resolve) => {
    const builder = new picker.PickerBuilder()
      .setOAuthToken(token)
      .setDeveloperKey(API_KEY)
      .setTitle('Choose from Google Drive');
    if (APP_ID) builder.setAppId(APP_ID);

    // Tab 1 (default): a navigable LIST — sortable Name / Owner / Last-modified
    // columns, opened inside the linked folder. Google labels it "Google Drive".
    const listView = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setMimeTypes(mimes)
      .setMode(picker.DocsViewMode.LIST);
    if (parentId) listView.setParent(parentId);
    builder.addView(listView);

    // Tab 2: a thumbnail GRID of your images (nicer for eyeballing pictures).
    // A distinct ViewId so Google labels it "Images" rather than a duplicate
    // "Google Drive" tab. (This view is image-only and not folder-scoped.)
    const gridView = new picker.DocsView(picker.ViewId.DOCS_IMAGES).setMode(
      picker.DocsViewMode.GRID,
    );
    builder.addView(gridView);

    builder
      .setCallback((data: any) => {
        if (data.action === picker.Action.PICKED) resolve(data.docs?.[0] ?? null);
        else if (data.action === picker.Action.CANCEL) resolve(null);
      })
      .build()
      .setVisible(true);
  });
  if (!doc) return null;

  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `Could not download that file from Drive (${res.status}). ${detail.slice(0, 300)}`.trim(),
    );
  }
  const blob = await res.blob();
  const mime = doc.mimeType || blob.type || 'application/octet-stream';
  const file = new File([blob], doc.name || 'drive-file', { type: mime });
  return { file };
}

/** Pick an image from Drive (images only — used by Compose). Opens inside the
 * linked folder when one is passed; orgId enables the eternal server token. */
export function pickImageFromDrive(
  parentId?: string | null,
  orgId?: string | null,
): Promise<DrivePick | null> {
  return pickFromDrive(['DOCS_IMAGES'], parentId, orgId);
}

/** Pick an image OR video from Drive (used by the Library). */
export function pickMediaFromDrive(
  parentId?: string | null,
  orgId?: string | null,
): Promise<DrivePick | null> {
  return pickFromDrive(['DOCS_IMAGES', 'DOCS_VIDEOS'], parentId, orgId);
}

export interface DriveFileRef {
  id: string;
  name: string;
  mimeType: string;
}

/**
 * Pick a VIDEO (reel) from Drive and return its METADATA only — no download. The
 * bytes stay in Drive; at ad-build time the worker streams them straight to Meta
 * (nothing imported into Supabase). Opens inside the linked folder when passed; the
 * app id is required so the least-privilege `drive.file` grant covers the pick.
 */
export async function pickDriveFileRef(
  parentId?: string | null,
  orgId?: string | null,
): Promise<DriveFileRef | null> {
  if (!driveConfigured) throw new Error('Google Drive is not configured.');
  await ensureReady();
  const token = await getToken(orgId);
  const picker = window.google.picker;
  const VIDEO_MIMES = 'video/mp4,video/quicktime,video/webm';

  const doc = await new Promise<any | null>((resolve) => {
    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setMimeTypes(VIDEO_MIMES)
      .setMode(picker.DocsViewMode.LIST);
    if (parentId) view.setParent(parentId);
    const builder = new picker.PickerBuilder()
      .setOAuthToken(token)
      .setDeveloperKey(API_KEY)
      .setTitle('Choose a reel from Google Drive')
      .addView(view);
    if (APP_ID) builder.setAppId(APP_ID);
    builder
      .setCallback((data: any) => {
        if (data.action === picker.Action.PICKED) resolve(data.docs?.[0] ?? null);
        else if (data.action === picker.Action.CANCEL) resolve(null);
      })
      .build()
      .setVisible(true);
  });
  if (!doc) return null;
  return {
    id: doc.id,
    name: doc.name || 'reel',
    mimeType: doc.mimeType || 'video/mp4',
  };
}

export interface DriveFolder {
  id: string;
  name: string;
  url: string;
}

/** Open the Drive picker in folder-select mode and return the chosen folder. */
export async function pickDriveFolder(orgId?: string | null): Promise<DriveFolder | null> {
  if (!driveConfigured) throw new Error('Google Drive is not configured.');
  await ensureReady();
  const token = await getToken(orgId);
  const picker = window.google.picker;

  const folder = await new Promise<any | null>((resolve) => {
    const view = new picker.DocsView(picker.ViewId.FOLDERS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes('application/vnd.google-apps.folder')
      // List mode keeps the sortable Name / Owner / Last-modified columns, which
      // matter more than thumbnails when hunting for the right folder to link.
      .setMode(picker.DocsViewMode.LIST);
    const fb = new picker.PickerBuilder()
      .setOAuthToken(token)
      .setDeveloperKey(API_KEY)
      .setTitle('Link a Google Drive folder')
      .addView(view);
    if (APP_ID) fb.setAppId(APP_ID);
    fb.setCallback((data: any) => {
      if (data.action === picker.Action.PICKED) resolve(data.docs?.[0] ?? null);
      else if (data.action === picker.Action.CANCEL) resolve(null);
    })
      .build()
      .setVisible(true);
  });
  if (!folder) return null;
  return {
    id: folder.id,
    name: folder.name || 'Google Drive folder',
    url: folder.url || `https://drive.google.com/drive/folders/${folder.id}`,
  };
}
